import {
  normaliseOrderProductCheckInInputField,
  normaliseNonOrderProductCheckInInputField
} from "src/helpers/productCheckInHelper";
import { hasWarehouseSplit, splitByWarehouseRatio } from "src/helpers";
import { IdGenerator } from "src/utils";
import { ProductCheckInFieldKey } from "src/enums";
import {
  OrderProductCheckInInputField,
  CheckInPurchaseOrderFormData,
  PostWarehouseCheckInPurchaseOrderRequestBody,
  NonOrderProductCheckInInputField
} from "src/types";
import { Product, PurchaseOrder } from "src/types";

export class CheckInPurchaseOrderMapper {
  public static initCheckInPurchaseOrderFormData(): CheckInPurchaseOrderFormData {
    return {
      asn: "",
      orderProductCheckIns: [],
      nonOrderProductCheckIns: [],
      defects: "",
      checkInImages: []
    };
  }

  public static orderProductCheckInInputFieldFromProduct(
    orderProduct: Product,
    confirmedQty?: number
  ): OrderProductCheckInInputField {
    const warehouseSplit = hasWarehouseSplit(orderProduct);
    const ukQty = warehouseSplit ? (orderProduct.ukQuantity ?? 0) : undefined;
    const euQty = warehouseSplit ? (orderProduct.euQuantity ?? 0) : undefined;

    const { uk: confirmedUkQty, eu: confirmedEuQty } = warehouseSplit
      ? splitByWarehouseRatio(confirmedQty, ukQty ?? 0, euQty ?? 0)
      : { uk: undefined, eu: undefined };

    return {
      receivedQty: "",
      expiryDates: [{ date: "", qty: "" }],
      fieldId: IdGenerator.generateUniqueElementId(orderProduct.id),
      fieldKey: ProductCheckInFieldKey.OrderProductCheckIn,
      productId: orderProduct.id,
      productName: orderProduct.name,
      productSkuName: orderProduct.skuName,
      productBarcode: orderProduct.barcode,
      productSize: (orderProduct as any).size,
      orderQty: orderProduct.quantity,
      confirmedQty: confirmedQty,
      hasWarehouseSplit: warehouseSplit,
      ukQty,
      euQty,
      confirmedUkQty,
      confirmedEuQty,
      receivedUkQty: "",
      receivedEuQty: ""
    };
  }

  public static orderProductCheckInInputFieldFromPurchaseOrder(
    purchaseOrder: PurchaseOrder
  ): OrderProductCheckInInputField[] {
    return (
      purchaseOrder.products?.map((product) =>
        CheckInPurchaseOrderMapper.orderProductCheckInInputFieldFromProduct(
          product,
          purchaseOrder.confirmationDetails?.products?.find(({ productId }) => productId === product.id)?.confirmedQty
        )
      ) ?? []
    );
  }

  public static initNonOrderProductCheckInInputField(hasWarehouseSplit = false): NonOrderProductCheckInInputField {
    return {
      receivedQty: "",
      receivedUkQty: "",
      receivedEuQty: "",
      hasWarehouseSplit,
      expiryDates: [{ date: "", qty: "" }],
      fieldId: IdGenerator.generateUniqueElementId(),
      fieldKey: ProductCheckInFieldKey.NonOrderProductCheckIn,
      productId: "",
      productName: ""
    };
  }

  public static checkInPurchaseOrderRequestBodyFromFormData(
    checkInPurchaseOrderFormData: CheckInPurchaseOrderFormData
  ): PostWarehouseCheckInPurchaseOrderRequestBody {
    const normalisedOrderProductCheckIns = checkInPurchaseOrderFormData.orderProductCheckIns.map(
      normaliseOrderProductCheckInInputField
    );

    const normalisedExtraProductCheckIns = checkInPurchaseOrderFormData.nonOrderProductCheckIns.map(
      normaliseNonOrderProductCheckInInputField
    );

    const normalisedDefects = checkInPurchaseOrderFormData.defects || undefined;

    return {
      timestamp: new Date().toISOString(),
      orderProductCheckIns: normalisedOrderProductCheckIns,
      extraProductCheckIns: normalisedExtraProductCheckIns.length ? normalisedExtraProductCheckIns : undefined,
      defects: normalisedDefects
    };
  }
}
